import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GoogleLogin } from '@react-oauth/google';

import Loader from '../components/Loader';
import ThemeAwareLogo from '../components/navbar/ThemeAwareLogo';
import {
  login,
  googleLogin,
  register,
  userSendMailChangePass,
  changeUserPassword,
} from '../actions/userActions';
import {
  USER_SEND_MAIL_CHANGE_PASS_RESET,
  USER_CHANGE_PASS_PROFILE_RESET,
} from '../constants/userConstants';
import { resolvePostAuthDestination } from '../utils/onboardingDestination';
import { getPasswordResetRequestFeedback } from './loginRecoveryFeedback';
import { normalizeRegisterErrorMessage } from './registerErrorFeedback';
import OtpCodeInput, { isCompleteOtpCode } from '../components/OtpCodeInput';
import { GOOGLE_OAUTH_ENABLED } from '../config/googleOAuth';

export default function LoginScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTarget = searchParams.get('next');

  // ---- Redux slices ----
  const {
    loading: loginLoading,
    error: loginError,
    success: loginSuccess,
    userInfo,
  } = useSelector((state) => state.userLogin);

  const {
    loading: registerLoading,
    error: registerError,
    success: registerSuccess,
  } = useSelector((state) => state.userRegister);

  const {
    loading: sendMailLoading,
    error: sendMailError,
    success: sendMailSuccess,
  } = useSelector((state) => state.userSendMailChangePass);

  const {
    loading: changePasswordLoading,
    error: changePasswordError,
    success: changePasswordSuccess,
  } = useSelector((state) => state.changePasswordUser);

  const [mode, setMode] = useState('login');
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState(1);
  const [passwordVisible, setPasswordVisible] = useState(false);

  // Recovery resend cooldown (seconds). 0 = ready, >0 = locked.
  const [resendCooldown, setResendCooldown] = useState(0);
  const canResend = resendCooldown === 0;
  const RESEND_COOLDOWN_SECONDS = 60;

  // ---- Login form ----
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // ---- Signup form ----
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('');

  // ---- Recovery form ----
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryNewPassword, setRecoveryNewPassword] = useState('');
  const [recoveryConfirmPassword, setRecoveryConfirmPassword] = useState('');

  // Password-pair validity (drives submit guards, disabled state, and aria/class flags).
  // "Show mismatch" requires both fields filled — empty confirm shouldn't flash invalid as user types.
  const signupPasswordsMatch = signupPassword === signupPasswordConfirm;
  const signupPasswordsFilled = signupPassword.length > 0 && signupPasswordConfirm.length > 0;
  const signupShowMismatch = signupPasswordsFilled && !signupPasswordsMatch;
  const signupSubmitDisabled = !signupPasswordsFilled || !signupPasswordsMatch;

  const recoveryPasswordsMatch = recoveryNewPassword === recoveryConfirmPassword;
  const recoveryPasswordsFilled =
    recoveryNewPassword.length > 0 && recoveryConfirmPassword.length > 0;
  const recoveryShowMismatch = recoveryPasswordsFilled && !recoveryPasswordsMatch;
  const recoverySubmitDisabled =
    !isCompleteOtpCode(recoveryCode) ||
    !recoveryPasswordsFilled ||
    !recoveryPasswordsMatch;

  // Hidden host for the real Google Sign-In button (visible Google buttons trigger it via click).
  const googleBtnRef = useRef(null);

  const isLogin = mode === 'login';
  const isSignup = mode === 'signup';

  const handleSetMode = (next) => {
    if (next !== 'login' && next !== 'signup') return;
    setMode(next);
    if (next !== 'login') setRecoveryOpen(false);
  };

  const handleToggleRecovery = () => {
    setRecoveryOpen((open) => {
      const next = !open;
      if (next) setRecoveryStep(1);
      return next;
    });
  };

  const handleCloseRecovery = () => setRecoveryOpen(false);

  const handleTogglePassword = () => setPasswordVisible((v) => !v);

  // ---- Submit handlers ----
  const handleLoginSubmit = (e) => {
    e.preventDefault();
    dispatch(login(loginEmail, loginPassword));
  };

  const handleRegisterSubmit = (e) => {
    e.preventDefault();
    if (!signupPasswordsMatch) {
      toast.error('Паролі не співпадають.');
      return;
    }
    dispatch(register(signupEmail, signupPassword));
  };

  // Google OAuth: visible buttons forward clicks to the hidden GoogleLogin host.
  const handleGoogleSuccess = (response) => {
    if (!response?.credential) {
      toast.error("Не вдалося отримати Google credential. Повторіть спробу пізніше.")
      return;
    }
    dispatch(googleLogin(response.credential));
  };

  const handleGoogleError = () => {
    toast.error('Не вдалося увійти через Google.');
  };

  const triggerGoogleSignIn = () => {
    if (!GOOGLE_OAUTH_ENABLED) {
      return;
    }
    const realBtn =
      googleBtnRef.current?.querySelector('div[role="button"]') ||
      googleBtnRef.current?.querySelector('button');
    if (realBtn) {
      realBtn.click();
    } else {
      toast.error('Google Sign-In ще не готовий. Спробуйте ще раз.');
    }
  };

  const handleSendRecoveryCode = () => {
    dispatch(userSendMailChangePass(recoveryEmail));
  };

  const handleResetPassword = () => {
    if (!isCompleteOtpCode(recoveryCode)) {
      toast.error('Перевірте чи ви ввели код правильно.');
      return;
    }
    if (!recoveryPasswordsMatch) {
      toast.error('Паролі не співпадають.');
      return;
    }
    dispatch(changeUserPassword(recoveryEmail, recoveryCode, recoveryNewPassword));
  };

  const stepIs = (n) => recoveryStep === n;
  const inertOf = (active) => (!active ? '' : undefined);

  // MM:SS formatter for the resend cooldown.
  const formatCooldown = (total) => {
    const mm = String(Math.floor(total / 60)).padStart(2, '0');
    const ss = String(total % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  // ---- Side effects ----

  // Redirect once user is authenticated (covers login, register, and googleLogin —
  // same userLogin slice). Canonical priority shared with useOnboardingRedirect:
  // unverified → email verification; verified incomplete → profile completion;
  // verified complete → optional validated /realty/:id return (Issue #290) or home.
  useEffect(() => {
    if (!userInfo) return;
    navigate(
      resolvePostAuthDestination(userInfo, { returnTarget }),
      { replace: true },
    );
  }, [userInfo, navigate, returnTarget]);

  // Login feedback (toast on error/success). Slice fields are reset by next dispatch, so no duplicate toast.
  useEffect(() => {
    if (loginError) {
      toast.error(loginError);
    } else if (loginSuccess) {
      toast.success('Ви успішно ввійшли в акаунт!');
    }
  }, [loginError, loginSuccess]);

  // Register feedback.
  useEffect(() => {
    if (registerError) {
      toast.error(normalizeRegisterErrorMessage(registerError));
    } else if (registerSuccess) {
      toast.success('Ви успішно створили акаунт!');
    }
  }, [registerError, registerSuccess]);

  // Recovery — code sent. Reset slice after handling so the toast doesn't refire on remount.
  useEffect(() => {
    if (sendMailError) {
      toast.error(sendMailError);
      dispatch({ type: USER_SEND_MAIL_CHANGE_PASS_RESET });
    } else if (sendMailSuccess) {
      const feedback = getPasswordResetRequestFeedback({
        sendMailSuccess: true,
        email: recoveryEmail,
      });
      toast.success(feedback.message);
      setRecoveryStep(feedback.advanceToStep);
      // (Re)start the resend cooldown — overwrites any in-flight value, so a repeat
      // success cleanly resets the timer without spawning a second interval.
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      dispatch({ type: USER_SEND_MAIL_CHANGE_PASS_RESET });
    }
  }, [sendMailError, sendMailSuccess, recoveryEmail, dispatch]);

  // Resend cooldown ticker. Depends on a boolean so the interval is created once
  // when the countdown starts and torn down once when it ends — not recreated each tick.
  // Functional updater keeps the closure fresh; cleanup handles unmount + completion.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recovery — password changed. Reset slice + close recovery + return to login mode.
  useEffect(() => {
    if (changePasswordError) {
      toast.error(changePasswordError);
      dispatch({ type: USER_CHANGE_PASS_PROFILE_RESET });
    } else if (changePasswordSuccess) {
      toast.success('Пароль успішно змінено');
      setRecoveryOpen(false);
      setRecoveryStep(1);
      setRecoveryCode('');
      setRecoveryNewPassword('');
      setRecoveryConfirmPassword('');
      dispatch({ type: USER_CHANGE_PASS_PROFILE_RESET });
    }
  }, [changePasswordError, changePasswordSuccess, dispatch]);

  return (
    <section className="login-section" id="Csection">
      <div className="auth-shell">
        <Link to="/login" className="auth-back">
          <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>На головну
        </Link>

        <div className={`auth-card${isSignup ? ' is-mode-signup' : ''}`} data-mode={mode}>
          {/* Mode state is driven by .is-mode-signup modifier on .auth-card (toggled in JS/React) */}

          {GOOGLE_OAUTH_ENABLED ? (
            <div ref={googleBtnRef} className="auth-google-host" aria-hidden="true">
              <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} />
            </div>
          ) : null}

          <header className="auth-card__head">
            <Link to="/login" className="auth-card__brand" aria-label="Рієстро">
              <ThemeAwareLogo
                className="auth-card__brand-logo theme-aware-logo"
                lightSrc="/images/logo-full-nobg.png"
                darkSrc="/images/logo-full-dark-nobg.png"
                alt="Рієстро"
              />
            </Link>
            <h1 className="auth-card__title">
              <span className="auth-card__title--login">Ласкаво просимо</span>
              <span className="auth-card__title--signup">Створіть акаунт</span>
            </h1>
            <p className="auth-card__subtitle">
              <span className="auth-card__subtitle--login">Увійдіть, щоб продовжити роботу з
                Рієстро.</span>
              <span className="auth-card__subtitle--signup">Кілька секунд — і у вас власний
                кабінет.</span>
            </p>
          </header>

          <div className="auth-tabs" role="tablist" aria-label="Вибір форми">
            <button
              type="button"
              id="login"
              className={`auth-tab${isLogin ? ' is-active' : ''}`}
              role="tab"
              data-mode="login"
              aria-selected={isLogin}
              aria-controls="auth-form-login"
              tabIndex={isLogin ? 0 : -1}
              onClick={() => handleSetMode('login')}
            >
              <i className="fa-solid fa-right-to-bracket" aria-hidden="true"></i>
              <span>Вхід</span>
            </button>
            <button
              type="button"
              id="signup"
              className={`auth-tab${isSignup ? ' is-active' : ''}`}
              role="tab"
              data-mode="signup"
              aria-selected={isSignup}
              aria-controls="auth-form-signup"
              tabIndex={isSignup ? 0 : -1}
              onClick={() => handleSetMode('signup')}
            >
              <i className="fa-solid fa-user-plus" aria-hidden="true"></i>
              <span>Реєстрація</span>
            </button>
          </div>

          {/* ============== LOGIN FORM ============== */}
          <form
            className={`auth-form auth-form--login ui-form${isLogin ? ' is-active' : ''}`}
            id="auth-form-login"
            action=""
            data-mode="login"
            aria-hidden={!isLogin}
            inert={inertOf(isLogin)}
            noValidate
            onSubmit={handleLoginSubmit}
          >
            {/* Recovery panel state is driven by .is-open modifier on .auth-recovery (toggled in JS/React) */}

            <div className="ap-row">
              <div className="ap-field ap-field--wide">
                <label htmlFor="login-email">Email <span className="req">*</span></label>
                <input id="login-email" name="email" type="email" className="ui-text-input" autoComplete="email"
                  required placeholder="you@example.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)} />
              </div>
            </div>

            <div className="ap-row">
              <div className="ap-field ap-field--wide">
                <label htmlFor="login-password">Пароль <span className="req">*</span></label>
                <div className="auth-password">
                  <input id="login-password" name="password" type={passwordVisible ? 'text' : 'password'}
                    className="ui-text-input" autoComplete="current-password" required placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)} />
                  <button type="button" className="auth-password__toggle login-password-toggle-icon"
                    aria-label="Показати або сховати пароль" onClick={handleTogglePassword}>
                    <i className={`fa-solid ${passwordVisible ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="auth-form__row">
              <label className="auth-checkbox">
                <input type="checkbox" name="remember" />
                <span className="auth-checkbox__box" aria-hidden="true">
                  <i className="fa-solid fa-check" aria-hidden="true"></i>
                </span>
                <span className="auth-checkbox__label">Запам&apos;ятати мене</span>
              </label>
              <button
                type="button"
                className="auth-form__forgot"
                aria-controls="auth-recovery-panel"
                aria-expanded={recoveryOpen}
                onClick={handleToggleRecovery}
              >Забули пароль?</button>
            </div>

            <button type="submit" className="action-btn action-btn--accent auth-form__submit">
              <i className="fa-solid fa-right-to-bracket" aria-hidden="true"></i>Увійти
            </button>

            {loginLoading && (
              <div className="auth-loader-overlay"><Loader /></div>
            )}

            {/* ============== INLINE PASSWORD RECOVERY ============== */}
            {/* DOM MUST persist (grid-template-rows transition needs the node mounted). Toggle .is-open. */}
            <section
              className={`auth-recovery${recoveryOpen ? ' is-open' : ''}`}
              id="auth-recovery-panel"
              aria-labelledby="auth-recovery-title"
              aria-hidden={!recoveryOpen}
              inert={inertOf(recoveryOpen)}
            >
              {(sendMailLoading || changePasswordLoading) && (
                <div className="auth-loader-overlay"><Loader /></div>
              )}
              <div className="auth-recovery__inner">
                <div className="auth-recovery__panel">
                  <header className="auth-recovery__head">
                    <div className="auth-recovery__head-text">
                      <h2 className="auth-recovery__title" id="auth-recovery-title">
                        Відновлення пароля
                      </h2>
                      <p className="auth-recovery__desc">
                        Виконайте 2 кроки, щоб задати новий пароль.
                      </p>
                    </div>
                    <button type="button" className="auth-recovery__close"
                      aria-label="Закрити відновлення пароля" onClick={handleCloseRecovery}>
                      <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                    </button>
                  </header>

                  <ol className="auth-recovery__steps">
                    {/* Step 1 — request code */}
                    <li
                      className={`auth-recovery__step${stepIs(1) ? ' is-active' : ''}`}
                      aria-hidden={!stepIs(1)}
                      inert={inertOf(stepIs(1))}
                    >
                      <div className="auth-recovery__step-head">
                        <span className="auth-recovery__step-num" aria-hidden="true">1</span>
                        <span className="auth-recovery__step-label">
                          Надішліть код на email
                        </span>
                      </div>
                      <div className="auth-recovery__step-body">
                        <div className="ap-field">
                          <label htmlFor="recovery-email">
                            Email <span className="req">*</span>
                          </label>
                          <input id="recovery-email" name="recovery_email" type="email"
                            className="ui-text-input" autoComplete="email" placeholder="you@example.com"
                            value={recoveryEmail}
                            onChange={(e) => setRecoveryEmail(e.target.value)} />
                        </div>
                        <div className="auth-recovery__send-row">
                          <button type="button" className="action-btn auth-recovery__send-btn"
                            onClick={handleSendRecoveryCode}
                            disabled={!canResend || sendMailLoading}>
                            <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>Надіслати код
                          </button>
                          {!canResend && (
                            <p className="auth-recovery__timer" aria-live="polite">
                              <i className="fa-regular fa-clock" aria-hidden="true"></i>
                              <span>Повторне надсилання через
                                <strong>{formatCooldown(resendCooldown)}</strong></span>
                            </p>
                          )}
                        </div>
                      </div>
                    </li>

                    {/* Step 2 — code + new password (matches backend request → confirm) */}
                    <li
                      className={`auth-recovery__step${stepIs(2) ? ' is-active' : ''}`}
                      aria-hidden={!stepIs(2)}
                      inert={inertOf(stepIs(2))}
                    >
                      <div className="auth-recovery__step-head">
                        <span className="auth-recovery__step-num" aria-hidden="true">2</span>
                        <span className="auth-recovery__step-label">
                          Введіть код і новий пароль
                        </span>
                      </div>
                      <div className="auth-recovery__step-body">
                        <div className="ap-field">
                          <label htmlFor="recovery-code">
                            Код підтвердження <span className="req">*</span>
                          </label>
                          <OtpCodeInput
                            id="recovery-code"
                            name="recovery_code"
                            className="ui-text-input"
                            placeholder="••••••"
                            value={recoveryCode}
                            onChange={setRecoveryCode}
                            data-testid="login-recovery-code"
                          />
                        </div>
                        <div className="ap-row ap-row--2">
                          <div className="ap-field">
                            <label htmlFor="recovery-new">
                              Новий пароль <span className="req">*</span>
                            </label>
                            <input id="recovery-new" name="recovery_new" type="password"
                              className={`ui-text-input${recoveryShowMismatch ? ' is-invalid' : ''}`}
                              autoComplete="new-password" minLength="8"
                              placeholder="Мін. 8 символів"
                              aria-invalid={recoveryShowMismatch}
                              value={recoveryNewPassword}
                              onChange={(e) => setRecoveryNewPassword(e.target.value)} />
                          </div>
                          <div className="ap-field">
                            <label htmlFor="recovery-new-confirm">
                              Повторіть новий пароль <span className="req">*</span>
                            </label>
                            <input id="recovery-new-confirm" name="recovery_new_confirm"
                              type="password"
                              className={`ui-text-input${recoveryShowMismatch ? ' is-invalid' : ''}`}
                              autoComplete="new-password"
                              minLength="8" placeholder="Повторіть пароль"
                              aria-invalid={recoveryShowMismatch}
                              value={recoveryConfirmPassword}
                              onChange={(e) => setRecoveryConfirmPassword(e.target.value)} />
                          </div>
                        </div>
                      </div>
                    </li>
                  </ol>

                  <button type="button" className="action-btn action-btn--accent auth-recovery__submit"
                    onClick={handleResetPassword}
                    disabled={recoverySubmitDisabled}>
                    <i className="fa-solid fa-check" aria-hidden="true"></i>
                    Підтвердити зміну пароля
                  </button>
                </div>
              </div>
            </section>
            {/* ===================================================== */}

            <div className="auth-divider" role="separator"><span>або</span></div>

            <button type="button" className="auth-google-btn" aria-label="Увійти через Google"
              disabled={!GOOGLE_OAUTH_ENABLED}
              aria-disabled={!GOOGLE_OAUTH_ENABLED}
              onClick={triggerGoogleSignIn}>
              <span className="auth-google-btn__icon" aria-hidden="true">
                <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#FFC107"
                    d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                  <path fill="#FF3D00"
                    d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                  <path fill="#4CAF50"
                    d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                  <path fill="#1976D2"
                    d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
                </svg>
              </span>
              <span>Продовжити з Google</span>
            </button>
          </form>

          {/* ============== SIGNUP FORM ============== */}
          <form
            className={`auth-form auth-form--signup ui-form${isSignup ? ' is-active' : ''}`}
            id="auth-form-signup"
            action=""
            data-mode="signup"
            aria-hidden={!isSignup}
            inert={inertOf(isSignup)}
            noValidate
            onSubmit={handleRegisterSubmit}
          >
            <div className="ap-row">
              <div className="ap-field ap-field--wide">
                <label htmlFor="signup-email">Email <span className="req">*</span></label>
                <input id="signup-email" name="email" type="email" className="ui-text-input" autoComplete="email"
                  required placeholder="you@example.com"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)} />
              </div>
            </div>

            <div className="ap-row ap-row--2">
              <div className="ap-field">
                <label htmlFor="signup-password">Пароль <span className="req">*</span></label>
                <input id="signup-password" name="password" type="password"
                  className={`ui-text-input${signupShowMismatch ? ' is-invalid' : ''}`}
                  autoComplete="new-password" required minLength="8" placeholder="Мін. 8 символів"
                  aria-invalid={signupShowMismatch}
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)} />
                <small className="form-hint">Не менше 8 символів.</small>
              </div>
              <div className="ap-field">
                <label htmlFor="signup-password-confirm">Підтвердьте пароль <span className="req">*</span></label>
                <input id="signup-password-confirm" name="password_confirm" type="password"
                  className={`ui-text-input${signupShowMismatch ? ' is-invalid' : ''}`}
                  autoComplete="new-password" required minLength="8" placeholder="Повторіть пароль"
                  aria-invalid={signupShowMismatch}
                  value={signupPasswordConfirm}
                  onChange={(e) => setSignupPasswordConfirm(e.target.value)} />
              </div>
            </div>

            <button type="submit" className="action-btn action-btn--accent auth-form__submit"
              disabled={signupSubmitDisabled}>
              <i className="fa-solid fa-user-plus" aria-hidden="true"></i>Створити акаунт
            </button>

            {registerLoading && (
              <div className="auth-loader-overlay"><Loader /></div>
            )}

            <div className="auth-divider" role="separator"><span>або</span></div>

            <button type="button" className="auth-google-btn" aria-label="Зареєструватися через Google"
              disabled={!GOOGLE_OAUTH_ENABLED}
              aria-disabled={!GOOGLE_OAUTH_ENABLED}
              onClick={triggerGoogleSignIn}>
              <span className="auth-google-btn__icon" aria-hidden="true">
                <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#FFC107"
                    d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                  <path fill="#FF3D00"
                    d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                  <path fill="#4CAF50"
                    d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                  <path fill="#1976D2"
                    d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
                </svg>
              </span>
              <span>Продовжити з Google</span>
            </button>
          </form>

          <p className="auth-card__foot">
            Натискаючи <strong>«Продовжити»</strong>, ви приймаєте
            <a href="privacy.html">політику конфіденційності</a>.
          </p>
        </div>
      </div>
    </section>
  );
}
